import { useMemo } from 'react';
import { PinnableItem, usePinnedOrder } from './usePinnedOrder';

const DANISH_LOCALE = 'da';
const DANISH_COLLATOR = new Intl.Collator(DANISH_LOCALE);
const FALLBACK_LETTER = '#';
const LETTER_REGEX = /\p{L}/u;

export type AlphabeticSection<T> = {
    letter: string;
    items: T[];
};

export type AlphabeticGroups<T> = {
    pinned: T[];
    sections: AlphabeticSection<T>[];
};

function firstLetterOf(label: string): string {
    const trimmed = label.trim().normalize('NFC');

    // Danish collation alphabetizes the old "Aa" digraph (e.g. "Aarhus") as "Å", sorting it after Z, so its section must match.
    if (/^aa/i.test(trimmed)) return 'Å';

    const char = trimmed.charAt(0);
    if (!char) return FALLBACK_LETTER;

    return LETTER_REGEX.test(char) ? char.toLocaleUpperCase(DANISH_LOCALE) : FALLBACK_LETTER;
}

/** Groups items under their first letter, reusing the filter list's own pin-to-top order; letters recompute from filtered matches while searching. */
export function useAlphabeticGroups<T extends PinnableItem>(
    items: T[],
    searchText: string,
    isOpen: boolean,
): AlphabeticGroups<T> {
    const pinnedOrder = usePinnedOrder(items, searchText, isOpen);

    return useMemo(() => {
        const visible = searchText
            ? items.filter((item) => item.label.toLowerCase().includes(searchText.toLowerCase()))
            : items;

        const pinnedSet = new Set(pinnedOrder);
        const pinned = pinnedOrder
            .map((value) => items.find((item) => item.value === value))
            .filter((item): item is T => !!item);

        const rest = visible.filter((item) => !pinnedSet.has(item.value));
        const sorted = [...rest].sort((a, b) => DANISH_COLLATOR.compare(a.label, b.label));

        const sections: AlphabeticSection<T>[] = [];
        for (const item of sorted) {
            const letter = firstLetterOf(item.label);
            const currentSection = sections.at(-1);

            if (currentSection?.letter === letter) {
                currentSection.items.push(item);
            } else {
                sections.push({ letter, items: [item] });
            }
        }

        return { pinned, sections };
    }, [items, searchText, pinnedOrder]);
}
